import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import Navbar from '../student/Navbar';
import AdminSidebar from './AdminSidebar';
import Loader from '../components/Loader';
import { motion } from 'framer-motion';
import {
  FaChartLine,
  FaUsers,
  FaEdit,
  FaEye,
  FaClock,
  FaSearch,
  FaFilter,
  FaDownload,
  FaExclamationTriangle,
  FaCheckCircle,
  FaTimesCircle,
  FaUser,
  FaTooth,
  FaFileMedical,
  FaClipboardCheck,
  FaImage,
  FaCalendarAlt,
  FaFlask,
  FaHistory,
  FaUserMd,
  FaXRay,
  FaFileAlt,
  FaMousePointer,
  FaDatabase,
  FaUpload,
  FaTrash,
  FaPlus,
  FaSave,
  FaChartBar,
  FaChartPie,
  FaChartArea,
  FaTrendingUp,
  FaTrendingDown,  
  FaGlobe,
  FaMobile,
  FaDesktop,
  FaTablet
} from 'react-icons/fa';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
} from 'chart.js';
import { Bar, Doughnut, Line, Pie, PolarArea } from 'react-chartjs-2';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
);

// Website color palette
const websiteColorPalette = {
  primary: '#0077B6',
  secondary: '#20B2AA',
  background: '#FFFFFF',
  text: '#333333',
  accent: '#28A745'
};

const ComprehensiveTrackingDashboard = () => {
  const { user, token } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('7d');
  const [selectedStudent, setSelectedStudent] = useState(null);
  
  // Data states
  const [overviewData, setOverviewData] = useState(null);
  const [activityData, setActivityData] = useState(null);
  const [editData, setEditData] = useState(null);
  const [students, setStudents] = useState([]);
  const [realTimeData, setRealTimeData] = useState([]);

  // Chart colors
  const chartColors = {
    primary: '#0077B6',
    secondary: '#20B2AA',
    success: '#28A745',
    warning: '#FFC107',
    danger: '#DC3545',
    info: '#17A2B8',
    light: '#F8F9FA',
    dark: '#343A40',
    gradient: ['#0077B6', '#20B2AA', '#28A745', '#FFC107', '#DC3545', '#17A2B8', '#6F42C1', '#E83E8C']
  };

  useEffect(() => {
    fetchAllData();
    const interval = setInterval(fetchRealTimeData, 30000); // Update every 30 seconds
    return () => clearInterval(interval);
  }, [timeRange, selectedStudent]);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchOverviewData(),
        fetchActivityData(),
        fetchEditData(),
        fetchStudents(),
        fetchRealTimeData()
      ]);
    } catch (err) {
      setError('Failed to load tracking data');
      console.error('Error fetching data:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchOverviewData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/admin-dashboard`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setOverviewData({
        activeStudents: response.data.totalStudents || 0,
        totalActivities: response.data.totalActivities || 0,
        errorCount: 0,
        activitiesByType: response.data.activitiesByType || []
      });
    } catch (err) {
      console.error('Error fetching overview data:', err);
      setOverviewData({ activeStudents: 0, totalActivities: 0, errorCount: 0 });
    }
  };

  const fetchActivityData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/activity-feed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Process activity data for charts
      const activities = response.data.activities || [];
      
      // Calculate activity trends by date
      const trendMap = {};
      const pageMap = {};
      const actionMap = {};
      
      activities.forEach(activity => {
        const date = activity.timestamp ? new Date(activity.timestamp).toLocaleDateString() : 'Unknown';
        trendMap[date] = trendMap[date] || { pageViews: 0, actions: 0 };
        trendMap[date].actions++;
        if (activity.action === 'page_view') trendMap[date].pageViews++;
        
        // Count pages
        const page = activity.details?.page || activity.page || 'Unknown';
        pageMap[page] = (pageMap[page] || 0) + 1;
        
        // Count actions
        const action = activity.action || 'Unknown';
        actionMap[action] = (actionMap[action] || 0) + 1;
      });
      
      setActivityData({
        activityTrends: Object.entries(trendMap).slice(-7).map(([date, data]) => ({
          date,
          pageViews: data.pageViews,
          actions: data.actions
        })),
        mostActivePages: Object.entries(pageMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([page, count]) => ({ page, count })),
        mostCommonActions: Object.entries(actionMap)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 10)
          .map(([action, count]) => ({ action, count }))
      });
    } catch (err) {
      console.error('Error fetching activity data:', err);
      setActivityData({ activityTrends: [], mostActivePages: [], mostCommonActions: [] });
    }
  };

  const fetchEditData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/edit-tracking?dateRange=${timeRange}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEditData({
        totalEdits: response.data.totalEdits || 0,
        editsByType: response.data.editsByType || {},
        editsByStudent: Object.entries(response.data.editsByStudent || {}).reduce((acc, [id, data]) => {
          acc[data.name || id] = data.count || 0;
          return acc;
        }, {}),
        recentEdits: response.data.recentEdits || [],
        errorCount: response.data.errorCount || 0
      });
    } catch (err) {
      console.error('Error fetching edit data:', err);
      setEditData({ totalEdits: 0, editsByType: {}, editsByStudent: {}, recentEdits: [] });
    }
  };

  const fetchStudents = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/admin/students?university=${encodeURIComponent(user?.university || '')}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudents(response.data || []);
    } catch (err) {
      console.error('Error fetching students:', err);
      setStudents([]);
    }
  };

  const fetchRealTimeData = async () => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/analytics/activity-feed`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRealTimeData(response.data.activities || []);
    } catch (err) {
      console.error('Error fetching real-time data:', err);
      setRealTimeData([]);
    }
  };

  const getActionIcon = (action) => {
    const iconMap = {
      'page_view': <FaEye className="text-blue-500" />,
      'button_click': <FaMousePointer className="text-green-500" />,
      'form_interaction': <FaEdit className="text-yellow-500" />,
      'data_operation': <FaDatabase className="text-purple-500" />,
      'search': <FaSearch className="text-indigo-500" />,
      'file_operation': <FaUpload className="text-orange-500" />,
      'modal_interaction': <FaEye className="text-pink-500" />,
      'navigation': <FaGlobe className="text-cyan-500" />,
      'error': <FaExclamationTriangle className="text-red-500" />,
      'patient_edit': <FaUser className="text-blue-600" />,
      'tooth_chart_edit': <FaTooth className="text-green-600" />,
      'sheet_edit': <FaFileMedical className="text-purple-600" />,
      'review_edit': <FaClipboardCheck className="text-orange-600" />,
      'file_edit': <FaImage className="text-pink-600" />,
      'creation': <FaPlus className="text-emerald-600" />,
      'deletion': <FaTrash className="text-red-600" />,
      'save_operation': <FaSave className="text-blue-600" />
    };
    return iconMap[action] || <FaChartBar className="text-gray-500" />;
  };

  const getPageIcon = (page) => {
    const iconMap = {
      'dashboard': <FaChartLine className="text-blue-500" />,
      'patients': <FaUser className="text-green-500" />,
      'appointments': <FaCalendarAlt className="text-purple-500" />,
      'toothchart': <FaTooth className="text-orange-500" />,
      'sheets': <FaFileMedical className="text-pink-500" />,
      'reviews': <FaClipboardCheck className="text-indigo-500" />,
      'gallery': <FaImage className="text-cyan-500" />,
      'xray': <FaXRay className="text-red-500" />,
      'medical': <FaUserMd className="text-emerald-500" />,
      'history': <FaHistory className="text-yellow-500" />,
      'lab': <FaFlask className="text-teal-500" />,
      'analytics': <FaChartBar className="text-violet-500" />,
      'consent': <FaFileAlt className="text-rose-500" />
    };
    return iconMap[page] || <FaGlobe className="text-gray-500" />;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleString();
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return `${diffInSeconds}s ago`;
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    return `${Math.floor(diffInSeconds / 86400)}d ago`;
  };

  // Chart configurations with fallback data
  const activityTrendLabels = activityData?.activityTrends?.length > 0 
    ? activityData.activityTrends.map(item => item.date) 
    : ['No Data'];
  const activityTrendChart = {
    labels: activityTrendLabels,
    datasets: [
      {
        label: 'Page Views',
        data: activityData?.activityTrends?.length > 0 
          ? activityData.activityTrends.map(item => item.pageViews) 
          : [0],
        borderColor: chartColors.primary,
        backgroundColor: `${chartColors.primary}20`,
        fill: true,
        tension: 0.4
      },
      {
        label: 'Actions',
        data: activityData?.activityTrends?.length > 0 
          ? activityData.activityTrends.map(item => item.actions) 
          : [0],
        borderColor: chartColors.secondary,
        backgroundColor: `${chartColors.secondary}20`,
        fill: true,
        tension: 0.4
      }
    ]
  };

  const pageActivityLabels = activityData?.mostActivePages?.length > 0 
    ? activityData.mostActivePages.map(item => item.page) 
    : ['No Data'];
  const pageActivityChart = {
    labels: pageActivityLabels,
    datasets: [
      {
        data: activityData?.mostActivePages?.length > 0 
          ? activityData.mostActivePages.map(item => item.count) 
          : [1],
        backgroundColor: activityData?.mostActivePages?.length > 0 
          ? chartColors.gradient 
          : ['rgba(200, 200, 200, 0.5)'],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const actionLabels = activityData?.mostCommonActions?.length > 0 
    ? activityData.mostCommonActions.map(item => item.action) 
    : ['No Data'];
  const actionDistributionChart = {
    labels: actionLabels,
    datasets: [
      {
        data: activityData?.mostCommonActions?.length > 0 
          ? activityData.mostCommonActions.map(item => item.count) 
          : [1],
        backgroundColor: activityData?.mostCommonActions?.length > 0 
          ? chartColors.gradient 
          : ['rgba(200, 200, 200, 0.5)'],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const editTypeLabels = editData?.editsByType && Object.keys(editData.editsByType).length > 0 
    ? Object.keys(editData.editsByType) 
    : ['No Data'];
  const editTypeChart = {
    labels: editTypeLabels,
    datasets: [
      {
        data: editData?.editsByType && Object.keys(editData.editsByType).length > 0 
          ? Object.values(editData.editsByType) 
          : [1],
        backgroundColor: editData?.editsByType && Object.keys(editData.editsByType).length > 0 
          ? chartColors.gradient 
          : ['rgba(200, 200, 200, 0.5)'],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const studentLabels = editData?.editsByStudent && Object.keys(editData.editsByStudent).length > 0 
    ? Object.keys(editData.editsByStudent) 
    : ['No Data'];
  const studentActivityChart = {
    labels: studentLabels,
    datasets: [
      {
        label: 'Edits',
        data: editData?.editsByStudent && Object.keys(editData.editsByStudent).length > 0 
          ? Object.values(editData.editsByStudent) 
          : [0],
        backgroundColor: chartColors.primary,
        borderColor: chartColors.primary,
        borderWidth: 1
      }
    ]
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top',
      },
      title: {
        display: true,
        text: 'Activity Overview'
      }
    }
  };

  if (loading) {
    return <Loader />;
  }

  if (error) {
    return (
      <div className="flex h-screen bg-gray-50">
        <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
        <div className="flex-1 flex flex-col overflow-hidden">
          <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
          <div className="flex-1 overflow-y-auto p-6">
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FaExclamationTriangle className="mx-auto h-12 w-12 text-red-500 mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Data</h3>
                <p className="text-gray-500">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-gray-50">
      <AdminSidebar isOpen={sidebarOpen} setIsOpen={setSidebarOpen} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Navbar toggleSidebar={() => setSidebarOpen(!sidebarOpen)} />
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-7xl mx-auto">
            {/* Header */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                <div>
                  <h1 className={`text-2xl sm:text-3xl font-bold text-[${websiteColorPalette.primary}] mb-2`}>
                    Comprehensive Tracking Dashboard
                  </h1>
                  <p className="text-gray-600">Monitor all student activities, edits, and system usage in real-time</p>
                </div>
                <div className="flex items-center space-x-4">
                  <select
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                    className={`px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[${websiteColorPalette.primary}] focus:border-[${websiteColorPalette.primary}]`}
                  >
                    <option value="1d">Last 24 Hours</option>
                    <option value="7d">Last 7 Days</option>
                    <option value="30d">Last 30 Days</option>
                    <option value="90d">Last 90 Days</option>
                    <option value="all">All Time</option>
                  </select>
                  <button
                    onClick={fetchAllData}
                    className={`px-4 py-2 bg-[${websiteColorPalette.primary}] text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center`}
                  >
                    <FaDownload className="mr-2" />
                    Refresh Data
                  </button>
                </div>
              </div>
            </motion.div>

            {/* Tab Navigation */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8"
            >
              <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <div className="flex">
                  {[
                    { id: 'overview', label: 'Overview', icon: FaChartBar },
                    { id: 'activity', label: 'Activity', icon: FaChartBar },
                    { id: 'edits', label: 'Edits', icon: FaEdit },
                    { id: 'realtime', label: 'Real-time', icon: FaClock }
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex items-center px-6 py-4 text-sm font-medium border-b-2 whitespace-nowrap transition-colors ${
                        activeTab === tab.id
                          ? `border-[${websiteColorPalette.primary}] text-[${websiteColorPalette.primary}] bg-blue-50`
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                    >
                      <tab.icon className="mr-2" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-blue-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                    <FaUsers className="h-5 w-5 sm:h-6 sm:w-6 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Active Students</p>
                    <p className="text-xl sm:text-2xl font-bold text-blue-600">{overviewData?.activeStudents || 0}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-green-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                    <FaChartBar className="h-5 w-5 sm:h-6 sm:w-6 text-green-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Total Activities</p>
                    <p className="text-xl sm:text-2xl font-bold text-green-600">{overviewData?.totalActivities || 0}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-purple-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                    <FaEdit className="h-5 w-5 sm:h-6 sm:w-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Total Edits</p>
                    <p className="text-xl sm:text-2xl font-bold text-purple-600">{editData?.totalEdits || 0}</p>
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="bg-white p-4 sm:p-6 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
              >
                <div className="flex items-center">
                  <div className="bg-red-50 w-12 h-12 sm:w-14 sm:h-14 rounded-lg flex items-center justify-center mr-3 sm:mr-4">
                    <FaExclamationTriangle className="h-5 w-5 sm:h-6 sm:w-6 text-red-600" />
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium text-gray-600">Errors</p>
                    <p className="text-xl sm:text-2xl font-bold text-red-600">{overviewData?.errorCount || 0}</p>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
              >
                <div className="p-4 sm:p-6">
                  <h3 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] mb-4 flex items-center`}>
                    <FaChartLine className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Activity Trends
                  </h3>
                  <div className="h-64 sm:h-80">
                    <Line data={activityTrendChart} options={chartOptions} />
                  </div>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 overflow-hidden"
              >
                <div className="p-4 sm:p-6">
                  <h3 className={`text-lg sm:text-xl font-bold text-[${websiteColorPalette.primary}] mb-4 flex items-center`}>
                    <FaChartPie className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
                    Page Activity
                  </h3>
                  <div className="h-64 sm:h-80">
                    <Doughnut data={pageActivityChart} options={chartOptions} />
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Activity Tab */}
        {activeTab === 'activity' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Activity Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Action Distribution</h3>
                <div className="h-64">
                  <Pie data={actionDistributionChart} options={chartOptions} />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Most Active Pages</h3>
                <div className="space-y-3">
                  {activityData?.mostActivePages?.slice(0, 8).map((page, index) => (
                    <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center">
                        {getPageIcon(page.page)}
                        <span className="ml-3 font-medium text-gray-900 capitalize">
                          {page.page.replace('_', ' ')}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <div className="w-24 bg-gray-200 rounded-full h-2 mr-3">
                          <div
                            className="bg-blue-600 h-2 rounded-full"
                            style={{ width: `${(page.count / Math.max(...activityData.mostActivePages.map(p => p.count))) * 100}%` }}
                          ></div>
                        </div>
                        <span className="text-sm font-medium text-gray-900">{page.count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </div>
          </motion.div>
        )}

        {/* Edits Tab */}
        {activeTab === 'edits' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            {/* Edit Charts */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Edits by Type</h3>
                <div className="h-64">
                  <PolarArea data={editTypeChart} options={chartOptions} />
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-lg shadow-sm p-6 border border-gray-200"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Student Edit Activity</h3>
                <div className="h-64">
                  <Bar data={studentActivityChart} options={chartOptions} />
                </div>
              </motion.div>
            </div>

            {/* Recent Edits Table */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Recent Edits</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Entity
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Edit Type
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Student
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Changes
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Time
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {editData?.recentEdits?.slice(0, 10).map((edit, index) => (
                      <tr key={index} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getActionIcon(edit.entityType)}
                            <span className="ml-2 text-sm font-medium text-gray-900 capitalize">
                              {edit.entityType.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {edit.editType.replace('_', ' ')}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {edit.studentName || 'Unknown'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {edit.fieldName ? `${edit.fieldName}: ${edit.changeType}` : edit.changeType}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {getTimeAgo(edit.timestamp)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Real-time Tab */}
        {activeTab === 'realtime' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-6"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-lg shadow-sm border border-gray-200"
            >
              <div className="px-6 py-4 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900">Real-time Activity Feed</h3>
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
                    Live
                  </div>
                </div>
              </div>
              <div className="max-h-96 overflow-y-auto">
                <div className="space-y-3 p-6">
                  {realTimeData.map((activity, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      className="flex items-start space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <div className="flex-shrink-0 mt-1">
                        {getActionIcon(activity.action)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between">
                          <p className="text-sm font-medium text-gray-900">
                            {activity.studentName || 'Unknown Student'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {getTimeAgo(activity.timestamp)}
                          </p>
                        </div>
                        <p className="text-sm text-gray-600">
                          {activity.details && typeof activity.details === 'string' 
                            ? JSON.parse(activity.details).page || activity.details
                            : activity.details?.page || 'Activity performed'
                          }
                        </p>
                        <div className="flex items-center mt-1">
                          <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {activity.action.replace('_', ' ').toUpperCase()}
                          </span>
                        </div>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ComprehensiveTrackingDashboard;
